import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import {
  PageTitleService,
  passwordConfirmationValidator,
  ValidationService,
} from '@tablesready/common-ui';
import { RestorePasswordService } from '../../services';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-restore-password',
  styleUrls: ['./restore-password.component.scss'],
  templateUrl: './restore-password.component.html',
})
export class RestorePasswordComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  sending = false;
  private readonly subscriptions = new Subscription();
  private code: string;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly restorePasswordService: RestorePasswordService,
    private readonly pageTitleService: PageTitleService,
    private readonly validationService: ValidationService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
  }

  ngOnInit(): void {
    const queryParamsSubscription = this.route.params.pipe(
      tap(({code}) => this.code = code),
      switchMap(() => this.restorePasswordService.checkCode(this.code)),
      tap(result => {
        if (!result) {
          this.router.navigate(['/login']);
        }
      }),
    ).subscribe();
    this.subscriptions.add(queryParamsSubscription);

    this.pageTitleService.setTitle('Restore password');
    this.form = this.formBuilder.group({
      password: ['', [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(255),
        this.validationService.passwordValidator()]
      ],
      passwordConfirmation: [''],
    });
    this.form.get('passwordConfirmation').setValidators([
      Validators.required,
      passwordConfirmationValidator(this.form.get('password'))
    ]);

    const valueChangesSubscription = this.form.get('password').valueChanges
      .pipe(tap(() => this.form.get('passwordConfirmation').updateValueAndValidity({onlySelf: true})))
      .subscribe();
    this.subscriptions.add(valueChangesSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit() {
    const {password} = this.form.value;
    this.restorePasswordService.setPassword(this.code, password)
      .pipe(
        tap(() => this.router.navigate(['/login'])),
        finalize(() => this.sending = false),
      )
      .subscribe();
  }
}
